/**
 * Pure helpers for translating browser KeyboardEvents into the wire
 * commands the Recording Studio backend understands.
 *
 * Extracted out of RecordModeModal so the logic can be unit-tested
 * without React/JSDOM — the rules around modifier flattening, host-
 * intercepted shortcuts, and standalone-modifier suppression are subtle
 * enough that they deserve dedicated coverage.
 *
 * Wire commands the backend accepts:
 *   { cmd: "type", text }   — printable single-char keystrokes
 *   { cmd: "press", key }   — named keys + modifier combos
 *
 * Combo key strings use Playwright's `keyboard.press` syntax:
 *   "Control+c", "Shift+Tab", "Control+Shift+ArrowLeft"
 */

export type RecordKeyCommand =
  | { cmd: "type"; text: string }
  | { cmd: "press"; key: string };

/**
 * Subset of `KeyboardEvent` we actually consume.  Defined explicitly so
 * the pure helpers can be exercised from tests without constructing a
 * real DOM event.
 */
export interface RecordKeyInput {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  repeat: boolean;
}

const STANDALONE_MODIFIERS = new Set(["Control", "Shift", "Alt", "Meta"]);

/**
 * Combos the host browser intercepts before our handler ever runs (or
 * that would kill the streaming page if they did reach the remote).
 * The keydown handler still preventDefaults so the host doesn't react
 * either — the user's intent was to drive the remote browser, not to
 * close their tab.
 *
 * Keys are normalised to lowercase for printable chars; non-printable
 * keys keep their `event.key` casing (e.g. "F11").  Match is exact on
 * the assembled combo string.
 */
const HOST_INTERCEPTED_COMBOS = new Set([
  "Control+t",
  "Control+w",
  "Control+n",
  "Control+Shift+t",
  "Control+Shift+n",
  "Control+q",
  "F5",
  "F11",
]);

/**
 * Build the wire command for a keydown event, or `null` if the event
 * shouldn't be forwarded (standalone modifier, repeated modifier hold,
 * host-intercepted shortcut).
 *
 * Mac `metaKey` (the Cmd key) is folded into Control on the wire.
 * Users expect Cmd+C to copy and Cmd+A to select-all — Playwright's
 * keyboard maps "Control+..." to the OS-native shortcut on Mac too, so
 * this is the right collapse.
 */
export function buildKeyCommand(e: RecordKeyInput): RecordKeyCommand | null {
  if (STANDALONE_MODIFIERS.has(e.key)) return null;
  if (e.repeat && STANDALONE_MODIFIERS.has(e.key)) return null;

  const hasModifier = e.ctrlKey || e.metaKey || e.altKey;

  // Plain printable keystrokes go through `type` so the page sees
  // composition events (matters for IME, autocomplete-aware inputs).
  if (!hasModifier && !e.shiftKey && e.key.length === 1) {
    return { cmd: "type", text: e.key };
  }

  // Shift+printable also goes through `type` — Shift just produces an
  // already-uppercased character that the underlying `e.key` carries.
  if (
    !e.ctrlKey &&
    !e.metaKey &&
    !e.altKey &&
    e.shiftKey &&
    e.key.length === 1
  ) {
    return { cmd: "type", text: e.key };
  }

  const parts: string[] = [];
  if (e.ctrlKey || e.metaKey) parts.push("Control");
  if (e.shiftKey) parts.push("Shift");
  if (e.altKey) parts.push("Alt");
  // Single printable chars normalise to lowercase for stable combo
  // strings (Playwright treats "Control+c" and "Control+C" the same,
  // but the dedup blocklist needs a canonical form).
  parts.push(e.key.length === 1 ? e.key.toLowerCase() : e.key);

  const combo = parts.join("+");
  if (HOST_INTERCEPTED_COMBOS.has(combo)) return null;
  return { cmd: "press", key: combo };
}

/**
 * Returns true if the event represents a copy or paste shortcut that
 * the studio's clipboard bridge should intercept.  Used to decide
 * whether to skip the keydown handler entirely (the DOM
 * `copy`/`cut`/`paste` event listeners on the wrapper handle these).
 */
export function isClipboardShortcut(
  e: RecordKeyInput
): "copy" | "paste" | "cut" | null {
  if (!(e.ctrlKey || e.metaKey)) return null;
  if (e.shiftKey || e.altKey) return null;
  const k = e.key.toLowerCase();
  if (k === "c") return "copy";
  if (k === "v") return "paste";
  if (k === "x") return "cut";
  return null;
}
